import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SAVE_ENDPOINT = 'https://api.wiseclean.ee/api:56Z3lZ-0/old_order/save_client_info';

function CustomerDetailsForm({ orderId, onSaved, onCritical }) {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [fieldError, setFieldError] = useState(null);
    const [submitError, setSubmitError] = useState(null);

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const nameValid = trimmedName.length >= 2;
    const emailValid = EMAIL_RE.test(trimmedEmail);
    const canSubmit = nameValid && emailValid && !loading;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) {
            if (!nameValid) setFieldError('name');
            else if (!emailValid) setFieldError('email');
            return;
        }
        setFieldError(null);
        setSubmitError(null);
        setLoading(true);

        try {
            const response = await fetch(SAVE_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    orderID: orderId,
                    name: trimmedName,
                    email: trimmedEmail,
                }),
            });
            const data = await response.json().catch(() => ({}));

            if (data && data.errorCode === 'critical_no_user_link') {
                onCritical();
                return;
            }
            if (!response.ok || !data.success) {
                setSubmitError(t('saveFailed'));
                return;
            }
            onSaved();
        } catch (err) {
            setSubmitError(t('saveFailed'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.page}>
            <header style={styles.header}>
                <img
                    src="/wiseclean-logo.png"
                    alt="WiseClean"
                    style={styles.logo}
                />
            </header>

            <main style={styles.main}>
                <form onSubmit={handleSubmit} style={styles.card} noValidate>
                    <div>
                        <h1 style={styles.title}>{t('contactDetailsTitle')}</h1>
                        <p style={styles.subtitle}>{t('contactDetailsSubtitle')}</p>
                    </div>

                    <label style={styles.label}>
                        <span style={styles.labelText}>{t('fullNameLabel')}</span>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t('fullNamePlaceholder')}
                            autoComplete="name"
                            className="wc-input"
                            style={{
                                ...styles.input,
                                ...(fieldError === 'name' ? styles.inputError : null),
                            }}
                            disabled={loading}
                        />
                        {fieldError === 'name' ? (
                            <span style={styles.errorText}>{t('nameTooShort')}</span>
                        ) : null}
                    </label>

                    <label style={styles.label}>
                        <span style={styles.labelText}>{t('emailLabel')}</span>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t('emailPlaceholder')}
                            autoComplete="email"
                            inputMode="email"
                            className="wc-input"
                            style={{
                                ...styles.input,
                                ...(fieldError === 'email' ? styles.inputError : null),
                            }}
                            disabled={loading}
                        />
                        {fieldError === 'email' ? (
                            <span style={styles.errorText}>{t('invalidEmail')}</span>
                        ) : null}
                    </label>

                    <button
                        type="submit"
                        disabled={!canSubmit}
                        style={{
                            ...styles.cta,
                            ...(canSubmit ? null : styles.ctaDisabled),
                        }}
                    >
                        {loading ? t('savingDetails') : t('continueToPayment')}
                    </button>

                    {submitError ? (
                        <p style={styles.submitError}>{submitError}</p>
                    ) : null}
                </form>
            </main>
        </div>
    );
}

const styles = {
    page: {
        minHeight: '100vh',
        background: 'var(--wc-bg-secondary)',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        padding: '20px 24px',
        display: 'flex',
        justifyContent: 'center',
    },
    logo: {
        height: '36px',
        width: 'auto',
        objectFit: 'contain',
    },
    main: {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        padding: '8px 20px 40px',
    },
    card: {
        width: '100%',
        maxWidth: '460px',
        background: 'var(--wc-bg-primary)',
        border: '1px solid var(--wc-border)',
        borderRadius: 'var(--wc-radius-card)',
        boxShadow: 'var(--wc-shadow-card)',
        padding: '28px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
    },
    title: {
        fontSize: '24px',
        lineHeight: 1.3,
        fontWeight: 700,
        color: 'var(--wc-text-primary)',
        margin: 0,
    },
    subtitle: {
        fontSize: '14px',
        lineHeight: 1.6,
        color: 'var(--wc-text-secondary)',
        margin: '6px 0 0 0',
    },
    label: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
    },
    labelText: {
        fontSize: '13px',
        fontWeight: 600,
        color: 'var(--wc-text-primary)',
    },
    input: {
        fontFamily: 'inherit',
        fontSize: '16px',
        padding: '12px 14px',
        border: '1px solid var(--wc-border)',
        borderRadius: 'var(--wc-radius-input)',
        outline: 'none',
        color: 'var(--wc-text-primary)',
        backgroundColor: 'var(--wc-bg-primary)',
        transition: 'border-color var(--wc-dur-fast) var(--wc-ease), box-shadow var(--wc-dur-fast) var(--wc-ease)',
    },
    inputError: {
        borderColor: 'var(--wc-error)',
    },
    errorText: {
        fontSize: '12px',
        color: 'var(--wc-error)',
    },
    cta: {
        marginTop: '4px',
        padding: '14px 16px',
        minHeight: '48px',
        fontSize: '16px',
        fontWeight: 600,
        fontFamily: 'inherit',
        color: '#fff',
        background: 'var(--wc-brand)',
        border: 'none',
        borderRadius: 'var(--wc-radius-btn)',
        boxShadow: 'var(--wc-shadow-cta)',
        cursor: 'pointer',
        transition: 'background var(--wc-dur-fast) var(--wc-ease)',
    },
    ctaDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
        boxShadow: 'none',
    },
    submitError: {
        fontSize: '14px',
        color: 'var(--wc-error)',
        textAlign: 'center',
        margin: 0,
    },
};

CustomerDetailsForm.propTypes = {
    orderId: PropTypes.string.isRequired,
    onSaved: PropTypes.func.isRequired,
    onCritical: PropTypes.func.isRequired,
};

export default CustomerDetailsForm;
